import { useState, useMemo, useCallback, useEffect } from 'react'
import { Badge, Button } from '@/ui'
import { cn } from '@/lib/cn'
import type { SectionDesign, Stylesheet } from './types'
import { groupBySectionType, parseCollapsedTypes } from './grouping'
import styles from '../SectionDesigns.module.css'

/** 접힌 sectionType 목록 저장 키 (AiQueueMonitor의 'ai-queue-monitor:refresh' 컨벤션). */
const COLLAPSED_KEY = 'section-library:collapsed'

export type LibrarySelection =
  | { kind: 'section'; id: string }
  | { kind: 'stylesheet'; id: string }
  | { kind: 'new-section' }
  | { kind: 'new-stylesheet' }
  | null

interface Props {
  sections: SectionDesign[]
  stylesheets: Stylesheet[]
  selection: LibrarySelection
  onSelect: (sel: LibrarySelection) => void
}

export function SectionLibrarySidebar({ sections, stylesheets, selection, onSelect }: Props) {
  const covers = sections.filter((s) => s.sectionType === 'cover')
  // 본문 섹션은 sectionType별 하위 그룹으로 묶는다. 입력은 이미 sortOrder → name 정렬됨.
  const bodyGroups = useMemo(
    () => groupBySectionType(sections.filter((s) => s.sectionType !== 'cover')),
    [sections],
  )

  // 기본 전체 펼침 — "접힌 것만" 저장하므로 새 sectionType이 생겨도 펼친 상태로 나타난다.
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => parseCollapsedTypes(localStorage.getItem(COLLAPSED_KEY)),
  )

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]))
  }, [collapsed])

  const toggleGroup = useCallback((sectionType: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(sectionType)) next.delete(sectionType)
      else next.add(sectionType)
      return next
    })
  }, [])

  const isSelected = (kind: 'section' | 'stylesheet', id: string) =>
    selection?.kind === kind && 'id' in selection && selection.id === id

  return (
    <div className={styles.sidebar}>
      {/* 전역 시트는 개수가 적고 자주 열어 보므로 최상단에 둔다(본문 섹션 목록에 밀려 스크롤되지 않게). */}
      <SectionGroup
        title="전역 스타일시트 (1:N 공유)"
        types={distinctTypes(stylesheets.map((s) => s.type))}
        onNew={() => onSelect({ kind: 'new-stylesheet' })}
      >
        {stylesheets.map((s) => (
          <SectionRow key={s.id} name={s.name} inactive={!s.isActive}
            active={isSelected('stylesheet', s.id)} onClick={() => onSelect({ kind: 'stylesheet', id: s.id })} />
        ))}
      </SectionGroup>

      <SectionGroup title="COVER (히어로)" types={['cover']} onNew={() => onSelect({ kind: 'new-section' })}>
        {covers.map((s) => (
          <SectionRow key={s.id} name={s.name} inactive={!s.isActive}
            active={isSelected('section', s.id)} onClick={() => onSelect({ kind: 'section', id: s.id })} />
        ))}
      </SectionGroup>

      <SectionGroup title="본문 섹션" onNew={() => onSelect({ kind: 'new-section' })}>
        {bodyGroups.map((g) => (
          <SectionTypeGroup
            key={g.sectionType}
            sectionType={g.sectionType}
            count={g.items.length}
            collapsed={collapsed.has(g.sectionType)}
            onToggle={() => toggleGroup(g.sectionType)}
          >
            {g.items.map((s) => (
              <SectionRow key={s.id} name={s.name} inactive={!s.isActive}
                indented active={isSelected('section', s.id)}
                onClick={() => onSelect({ kind: 'section', id: s.id })} />
            ))}
          </SectionTypeGroup>
        ))}
      </SectionGroup>
    </div>
  )
}

/** 중복 제거된 타입 목록(입력 순서 유지) — 그룹 헤더 배지용. */
function distinctTypes(types: string[]): string[] {
  return [...new Set(types)]
}

/**
 * 사이드바 그룹. types 를 주면 그룹 라벨 오른쪽에 타입 배지를 단다.
 * 그룹 안의 행은 모두 같은 타입이므로 행별로 배지를 반복하지 않는다.
 * (본문 섹션은 하위 그룹 헤더 자체가 sectionType 이라 types 를 주지 않는다.)
 */
function SectionGroup({ title, types, onNew, children }: {
  title: string; types?: string[]; onNew: () => void; children: React.ReactNode
}) {
  return (
    <div className={styles.sidebarGroup}>
      <div className={styles.sidebarGroupHeader}>
        <span className={styles.sidebarGroupLabel}>
          <span className={styles.sidebarGroupTitle}>{title}</span>
          {types?.map((t) => <Badge key={t} color="blue">{t}</Badge>)}
        </span>
        <Button variant="ghost" size="sm" onClick={onNew}>+ 새</Button>
      </div>
      {children}
    </div>
  )
}

/**
 * sectionType 하위 그룹 — 헤더 전체가 접기 토글. 헤더에 다른 버튼을 두지 않아 중첩 버튼이 없다.
 * 헤더 라벨 자체가 sectionType 이므로 대문자 텍스트 대신 타입 배지로 보여준다(행별 배지 반복 없음).
 */
function SectionTypeGroup({ sectionType, count, collapsed, onToggle, children }: {
  sectionType: string; count: number; collapsed: boolean; onToggle: () => void; children: React.ReactNode
}) {
  return (
    <div className={styles.typeGroup}>
      <button type="button" className={styles.typeGroupHeader} onClick={onToggle} aria-expanded={!collapsed}>
        <span className={styles.typeGroupCaret} aria-hidden="true">{collapsed ? '▶' : '▼'}</span>
        <Badge color="blue">{sectionType}</Badge>
        <span className={styles.typeGroupCount}>({count})</span>
      </button>
      {!collapsed && children}
    </div>
  )
}

/** 타입 배지는 그룹 헤더가 갖는다 — 행에는 이름과 비활성 표시만. */
function SectionRow({ name, inactive, active, indented, onClick }: {
  name: string; inactive: boolean; active: boolean; indented?: boolean; onClick: () => void
}) {
  return (
    <button
      type="button"
      className={cn(styles.sidebarRow, indented && styles.sidebarRowIndented, active && styles.sidebarRowActive)}
      onClick={onClick}
    >
      <span className={styles.sidebarRowName}>{name}</span>
      {inactive && (
        <span className={styles.sidebarRowMeta}>
          <Badge color="yellow">비활성</Badge>
        </span>
      )}
    </button>
  )
}
